import { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { NAV } from '../constants/testIds';
import { Menu, X } from 'lucide-react';

const links = [
  { to: '/relocation',  label: 'Relocation',  testId: 'nav-pillar-relocation',  accent: true },
  { to: '/real-estate', label: 'Real Estate', testId: 'nav-pillar-real-estate', accent: true },
  { to: '/finance',     label: 'Finance',     testId: 'nav-pillar-finance',     accent: true },
  { to: '/logistics',   label: 'Logistics',   testId: 'nav-pillar-logistics',   accent: true },
  { to: '/academy',     label: 'Academy',     testId: 'nav-academy-link' },
  { to: '/affiliates',  label: 'Our Affiliates', testId: 'nav-affiliates-link' },
  { to: '/vault',       label: 'The Vault',   testId: NAV.vaultLink },
];

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#050505]/85 border-b border-[#D4AF37]/10">
      <div className="px-6 md:px-12 lg:px-16 py-5 flex items-center justify-between">
        <Link to="/" data-testid={NAV.brand} className="group flex items-baseline gap-3" strokeWidth={1}>
          <span className="text-[10px] tracking-[0.4em] text-[#D4AF37] uppercase">EIGOO</span>
          <span className="text-base tracking-[0.3em] uppercase font-light group-hover:text-[#D4AF37] transition-colors">
            LIVE FREE
          </span>
          <span data-testid="nav-empowered-by-eigoo" className="hidden xl:inline text-[9px] tracking-[0.4em] uppercase text-[#A3A3A3] pl-3 ml-3 border-l border-[#D4AF37]/30">
            Sovereign Relocation Hub
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testId}
              className={({ isActive }) =>
                `text-[11px] tracking-[0.25em] uppercase font-light transition-colors ${
                  l.accent
                    ? (isActive ? 'text-[#D4AF37] border-b border-[#D4AF37]' : 'text-[#D4AF37] hover:opacity-80')
                    : (isActive ? 'text-[#D4AF37]' : 'text-[#A3A3A3] hover:text-[#F5F5F5]')
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          {user ? (
            <div className="flex items-center gap-6 pl-6 border-l border-[#D4AF37]/20">
              <NavLink
                to="/dashboard"
                data-testid={NAV.dashboardLink}
                className="text-[11px] tracking-[0.25em] uppercase text-[#D4AF37] hover:text-[#F5F5F5] transition-colors"
              >
                Command Deck
              </NavLink>
              <button
                onClick={handleLogout}
                data-testid={NAV.logoutBtn}
                className="text-[11px] tracking-[0.25em] uppercase text-[#A3A3A3] hover:text-[#D4AF37] transition-colors"
              >
                Disembark
              </button>
            </div>
          ) : (
            <NavLink
              to="/login"
              data-testid={NAV.loginLink}
              className="text-[11px] tracking-[0.25em] uppercase text-[#D4AF37] border border-[#D4AF37]/40 px-5 py-2 hover:bg-[#D4AF37] hover:text-[#050505] transition-all"
            >
              Sign In
            </NavLink>
          )}
        </div>

        <button
          onClick={() => setOpen(!open)}
          data-testid={NAV.mobileToggle}
          className="lg:hidden text-[#D4AF37]"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} strokeWidth={1} /> : <Menu size={22} strokeWidth={1} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#D4AF37]/10 bg-[#050505]/95 backdrop-blur-xl px-6 py-8 space-y-6">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `block text-sm tracking-[0.25em] uppercase ${
                  isActive ? 'text-[#D4AF37]' : 'text-[#A3A3A3]'
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          {user ? (
            <>
              <NavLink to="/dashboard" onClick={() => setOpen(false)} className="block text-sm tracking-[0.25em] uppercase text-[#D4AF37]">
                Command Deck
              </NavLink>
              <button onClick={handleLogout} className="block text-sm tracking-[0.25em] uppercase text-[#A3A3A3]">
                Disembark
              </button>
            </>
          ) : (
            <NavLink to="/login" onClick={() => setOpen(false)} className="block text-sm tracking-[0.25em] uppercase text-[#D4AF37]">
              Sign In
            </NavLink>
          )}
        </div>
      )}
    </nav>
  );
};
